import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  CustomModal,
  DesktopSidebar,
  LoadingSpinner,
  NameDialog,
  PageHeader,
  PageNotFound,
} from "./components";
import { AppProviders } from "./contexts/AppProviders";
import { useDialogContext } from "./contexts/DialogContext";
import { useExportContext } from "./contexts/ExportContext";
import { useModalContext } from "./contexts/ModalContext";
import { useUIContext } from "./contexts/UIContext";
import { useUploadContext } from "./contexts/UploadContext";
import {
  CustomAnalysisForm,
  DataExportProgressModal,
  FileUploadProgressModal,
  HelpScoutBeacon,
  PlayerGroupModal,
  RebootLiveInterface,
  VisualizerModal,
} from "./features";
import { REBOOT_LIVE_ACCESS_PERMISSION } from "./features/RebootLive/constants";
import {
  AggregateReportGroup,
  Analyze,
  CompletedSessions,
  Develop,
  HomeDashboard,
  PlayerDetails,
  Players,
  ProcessingSessions,
  Reports,
  SessionDetails,
  Sessions,
} from "./pages";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  clearUserStateAsync,
  selectUserPermissions,
  userSlice,
} from "./redux/slices/userSlice";
import { fetchUserPermissions, useAuth0 } from "./shared/Auth0Utility";

declare global {
  interface Window {
    Beacon: any;
    userGuiding: any;
  }
}

const AppContent: React.FC = () => {
  const { user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  // AppContext
  const { displayBack } = useUIContext();
  const { modalState, resetModalState } = useModalContext();
  const { dialogState } = useDialogContext();
  const { uploadProgressState } = useUploadContext();
  const { exportProgressState } = useExportContext();
  const permissions = useAppSelector(selectUserPermissions);

  useEffect(() => {
    if (location.pathname === "/login") {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const closeModal = useCallback(() => {
    const title = modalState.title;
    resetModalState(title);
  }, [modalState.title, resetModalState]);

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      <Toaster containerStyle={{ zIndex: 999999 }} />
      <div className="flex flex-col h-full w-full overflow-auto">
        <CustomModal modalState={modalState} closeModal={closeModal} />
        <PlayerGroupModal />
        <VisualizerModal />
        {(dialogState.type === "analysis" || dialogState.type === "group") &&
          dialogState.shouldOpen && <NameDialog />}
        <div
          className="fixed z-10 h-16 bg-white w-full"
          data-testid="header-parent-container"
        >
          <div className="w-full h-full">
            {user && (
              <PageHeader
                user={user}
                displayBack={displayBack}
                data-testid="header-component"
              />
            )}
          </div>
        </div>
        <div className="flex h-screen w-full mt-16">
          <DesktopSidebar />
          <Routes>
            <Route path={"/"} element={<HomeDashboard />} />
            <Route path={"/reports/:activeTab?"} element={<Reports />} />
            <Route
              path={"/aggregate-group/:reportId"}
              element={<AggregateReportGroup />}
            />
            <Route path={"/sessions"} element={<Sessions />} />
            <Route path={"/players/:activeTab?"} element={<Players />} />
            <Route
              path={"/processing-sessions"}
              element={<ProcessingSessions />}
            />
            <Route
              path={"/completed-sessions"}
              element={<CompletedSessions />}
            />
            <Route path={"/player/:playerId"} element={<PlayerDetails />} />
            <Route path={"/session/:sessionId"} element={<SessionDetails />} />
            <Route path={"/analyze/:activeTab?"} element={<Analyze />} />
            <Route
              path={"/request-analysis"}
              element={<CustomAnalysisForm />}
            />
            <Route path={"/develop/:activeTab?"} element={<Develop />} />
            {permissions.includes(REBOOT_LIVE_ACCESS_PERMISSION) && (
              <Route path={"/reboot-live"} element={<RebootLiveInterface />} />
            )}
            <Route path={"/error/:code"} element={<PageNotFound />} />
            <Route path="*" element={<Navigate to="/error/404" replace />} />
          </Routes>
        </div>
        <FileUploadProgressModal title={uploadProgressState.title} />
        <DataExportProgressModal
          title={
            exportProgressState.title
              ? exportProgressState.title
              : "Preparing Export"
          }
        />
      </div>
      <HelpScoutBeacon />
    </div>
  );
};

export const App: React.FC = () => {
  const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const dispatch = useAppDispatch();
  const { setUserCredentials } = userSlice.actions;

  useEffect(() => {
    (async function login() {
      if (!isLoading && !user && !isAuthenticated) {
        await dispatch(clearUserStateAsync());
        await loginWithRedirect();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(setUserCredentials(user));
      fetchUserPermissions(dispatch);

      // If env-stage or env-prod, identify user with UserGuiding and Sentry.
      if (
        import.meta.env.VITE_RUN_ENV === "env-stage" ||
        import.meta.env.VITE_RUN_ENV === "env-prod"
      ) {
        window.userGuiding.identify(user.sub, {
          name: user.name,
          email: user.email,
        });
        Sentry.setUser({ email: user.email });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  return (
    <>
      {isAuthenticated ? (
        <AppProviders>
          <AppContent />
        </AppProviders>
      ) : (
        <div className="h-screen flex justify-center items-center animate-pulse">
          <LoadingSpinner />
        </div>
      )}
    </>
  );
};
